import type {
  ClientAdProductType,
  PortfolioAnalyticsPropertyResponseDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import { DisplayingRegionLevel } from '../../../../types/DisplayingRegionLevel';
import { useEffect, useState } from 'react';
import { formatCurrency } from '../../../../utils/formatCurrency';
import { getAllAdSpendMapping } from './AdSpendMap.utils';
import type { MapDotPayload } from '../../../SharedComponents/Map/Dots/Dots';

export const regionsWithMarkers = [
  DisplayingRegionLevel.METRO_AREA,
  DisplayingRegionLevel.CITY,
  DisplayingRegionLevel.ZIP,
];

export const useAdSpendMapData = () => {
  const [hiddenPackages, setHiddenPackages] = useState<Array<ClientAdProductType>>([]);
  const {
    filteredProperties,
    selectedRegionId,
    selectedDisplayingRegionLevel,
    visibleRegionIds,
    displayMode,
    allProperties,
    isFetchingInitialData,
  } = useMainLayout();

  const [adSpendMapping, setAdSpendMapping] = useState<Map<number, number> | null>(null);
  const [isCalculatingAdSpend, setIsCalculatingAdSpend] = useState(false);

  useEffect(() => {
    if (!isFetchingInitialData && allProperties && allProperties?.length > 0 && !adSpendMapping) {
      setIsCalculatingAdSpend(true);
      setAdSpendMapping(getAllAdSpendMapping(allProperties));
      setIsCalculatingAdSpend(false);
    }
  }, [allProperties, isFetchingInitialData, adSpendMapping]);

  const finalDisplayMode = selectedRegionId ? selectedDisplayingRegionLevel : displayMode;

  const allVisibleProperties = filteredProperties.filter((property) =>
    visibleRegionIds.some(
      (region) =>
        region.regionId === property.propertyDetails.stateRegionId ||
        region.regionId === property.propertyDetails.metroStatRegionId ||
        region.regionId === property.propertyDetails.cityRegionId ||
        region.regionId === property.propertyDetails.zipRegionId,
    ),
  );

  const propertiesByPackageType = allVisibleProperties.reduce(
    (acc, property) => {
      const packageType =
        property.aggregateStats.adproductTypes.length > 0 ? property.aggregateStats.adproductTypes[0] : null;
      if (packageType) {
        if (!acc[packageType]) {
          acc[packageType] = [];
        }
        acc[packageType].push(property);
      }
      return acc;
    },
    {} as Record<ClientAdProductType, Array<PortfolioAnalyticsPropertyResponseDto>>,
  );

  const allPackages: Array<ClientAdProductType> = Object.keys(propertiesByPackageType).map(
    (packageType) => packageType as ClientAdProductType,
  );

  const propertiesToRender = allPackages
    .filter((packageType) => !hiddenPackages.includes(packageType))
    .map((packageType) => {
      const properties = propertiesByPackageType[packageType as ClientAdProductType];
      return properties.map((property) => ({
        ...property,
        packageType: packageType as ClientAdProductType,
      }));
    })
    .flat();

  const renderDotNumber = (regionId: number, _: number) => {
    const adSpendForRegionProperties = adSpendMapping?.get(regionId) ?? 0;

    return formatCurrency(adSpendForRegionProperties);
  };

  const allDotsMap = new Map<string, MapDotPayload>();
  if (regionsWithMarkers.includes(finalDisplayMode)) {
    propertiesToRender.forEach((property) => {
      const propertyKey = `${property.propertyDetails.lat}-${property.propertyDetails.lon}`;
      if (!allDotsMap.has(propertyKey)) {
        allDotsMap.set(propertyKey, { type: 'package', data: property });
      }
    });
  }

  const allDots = Array.from(allDotsMap.entries()).map(([key, payload]) => ({ key, payload }));

  return {
    hiddenPackages,
    setHiddenPackages,
    allPackages,
    finalDisplayMode,
    propertiesByPackageType,
    propertiesToRender,
    renderDotNumber,
    isCalculatingAdSpend,
    allDots,
  };
};
